import React, { useEffect, useState } from 'react';
import { dataForVacationApplicationSupervisorTable, sortingDate } from 'shared/helpers/utils';
import { Badge, notification, Popconfirm, Skeleton, Spin } from 'antd';
import { viewerModel } from 'entities/viewer';
import { useAppSelector } from 'app/store';
import { RequestsSearch } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { RequestStatusType, SortCondition } from 'shared/enums';
import { SearchKeysNews } from 'shared/enums/news';
import { getCurrentDivision } from 'entities/divisions/model';
import { TableVacationApplication } from 'features/tables/supervisor/TableVacationApplication';
import { vacationApplicationSupervisorApi } from 'app/services/edpApiV1/supervisor/vacationApplication';
import { TabKeys } from 'features/divisions';
import { getVacationApplicationSupervisor } from 'entities/supervisor/vacationApplication';
import { ColumnsType } from 'antd/lib/table';
import { DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import { VacationModal } from 'features/vacations/vacationModal';
import { LoadingOutlined } from '@ant-design/icons';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import style from './index.module.scss';

export type TPreparedVacationSupervisorInfoData = {
  key: number;
  id: number;
  fio: string;
  vacationType: string;
  type: string;
  startedAt: string;
  endedAt: string;
  amountDays: number;
  employeeId?: number;
  status: {
    name: string;
    type: RequestStatusType;
  };
};

export const VacationPull = () => {
  const [page, setCurrentPage] = useState<number>(1);
  const [size, setCurrentSize] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const [requestToReject, setRequestToReject] = useState<number>(0);
  const [employeeToReject, setEmployeeToReject] = useState<number>();
  const [currentTakeToWorkVA, setCurrentTakeToWorkVA] = useState<number[]>([]);

  const { currentOption } = useAppSelector(getCurrentDivision);

  const [getVacationsDeputy] = vacationApplicationSupervisorApi.useLazyVacationsDeputyQuery();
  const [getVacationsActing] = vacationApplicationSupervisorApi.useLazyVacationsActingQuery();
  const [getVacationsSupervisor] = vacationApplicationSupervisorApi.useLazyVacationsSupervisorQuery();

  const [approveVacationDeputy, { isLoading: isDeputyApproveLoading }] =
    vacationApplicationSupervisorApi.useApproveVacationDeputyMutation();
  const [approveVacationActing, { isLoading: isActingApproveLoading }] =
    vacationApplicationSupervisorApi.useApproveVacationActingMutation();
  const [approveVacationSupervisor, { isLoading: isSupervisorApproveLoading }] =
    vacationApplicationSupervisorApi.useApproveVacationSupervisorMutation();

  const [rejectVacationDeputy, { isLoading: isDeputyRejectLoading }] =
    vacationApplicationSupervisorApi.useRejectVacationDeputyMutation();
  const [rejectVacationActing, { isLoading: isActingRejectLoading }] =
    vacationApplicationSupervisorApi.useRejectVacationActingMutation();
  const [rejectVacationSupervisor, { isLoading: isSupervisorRejectLoading }] =
    vacationApplicationSupervisorApi.useRejectVacationSupervisorMutation();

  const { data, pagination, loading, success, error } = useAppSelector(getVacationApplicationSupervisor);

  const refetch = async () => {
    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await getVacationsActing({
            isArchive: false,
            page,
            size,
            divisionId: currentOption.value,
            ...search,
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await getVacationsDeputy({
            isArchive: false,
            page,
            size,
            divisionId: currentOption.value,
            ...search,
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await getVacationsSupervisor({ isArchive: false, page, size, ...search }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  useEffect(() => {
    refetch();
  }, [currentOption, search, page, size]);

  const approve = async (requestId: number, employeeId?: number) => {
    setCurrentTakeToWorkVA([...currentTakeToWorkVA, requestId]);

    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await approveVacationActing({ divisionId: currentOption.value, requestId }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await approveVacationDeputy({ requestId, divisionId: currentOption.value }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await approveVacationSupervisor({ requestId, employeeId }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    setCurrentTakeToWorkVA(currentTakeToWorkVA.filter(elem => elem === requestId));
  };

  const reject = (requestId: number, employeeId?: number) => {
    setOpenModal(true);
    setRequestToReject(requestId);
    setEmployeeToReject(employeeId);
  };

  const handleSubmitRejectModal = async (rejectionReason: string) => {
    setCurrentTakeToWorkVA([...currentTakeToWorkVA, requestToReject]);

    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await rejectVacationActing({
            divisionId: currentOption.value,
            requestId: requestToReject,
            rejectionReason,
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await rejectVacationDeputy({
            requestId: requestToReject,
            divisionId: currentOption.value,
            rejectionReason,
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await rejectVacationSupervisor({
            requestId: requestToReject,
            employeeId: employeeToReject,
            rejectionReason,
          }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
    setCurrentTakeToWorkVA(currentTakeToWorkVA.filter(elem => elem === requestToReject));
  };

  const columns: ColumnsType<TPreparedVacationSupervisorInfoData> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },

    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },

    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: { type?: RequestStatusType; name: string }) => (
        <span>
          <Badge className={style.badge} status={status.type ? DayOffsStatusTypesMap[status.type] : 'processing'} />
          {status?.name}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'id',
      key: 'action',
      render: (id, data) => {
        return (
          <>
            {data.status.type === RequestStatusType.consideredBySupervisor && (
              <>
                {currentTakeToWorkVA.includes(Number(id)) &&
                (isDeputyApproveLoading ||
                  isActingApproveLoading ||
                  isSupervisorApproveLoading ||
                  isDeputyRejectLoading ||
                  isActingRejectLoading ||
                  isSupervisorRejectLoading) ? (
                  <Spin indicator={<LoadingOutlined spin />} />
                ) : (
                  <>
                    <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => approve(id, data.employeeId)}>
                      <a>Согласовать</a>
                    </Popconfirm>
                    <br />
                    <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => reject(id, data.employeeId)}>
                      <a className={style.reject}>Отклонить</a>
                    </Popconfirm>
                  </>
                )}
              </>
            )}
          </>
        );
      },
    },
  ];

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  // useNotifications(successTakeToWork, errorTakeToWork, 'Взято в работу', 'Что-то пошло не так', resetTakeToWork);
  // useIndicators(isSuccess, isFetching, false);

  const onRowClick = (data: any) => {};

  return (
    <>
      <VacationModal id="1" openModal={openModal} setOpenModal={setOpenModal} handleSubmit={handleSubmitRejectModal} />
      {data && columns && (
        <TableVacationApplication
          dataSource={dataForVacationApplicationSupervisorTable(data || [])}
          columns={columns}
          currentPage={page}
          setCurrentPage={setCurrentPage}
          currentSize={size}
          setCurrentSize={setCurrentSize}
          total={pagination?.total}
          onSearch={onSearch}
          onRowClick={onRowClick}
          loading={loading}
        />
      )}
    </>
  );
};
